<template>
  <v-card
    outlined
    class="unsaved-list mb-6"
  >
    <template v-for="(group, gIdx) in monthGroups">
      <div :key="`month-${group.key}`">
        <div class="month-label px-4 py-2">
          {{ group.label }}
        </div>

        <div
          v-for="row in group.rows"
          :key="`day-${row.business_date}`"
          class="day-row px-4 py-3"
        >
          <div class="day-main">
            <div class="d-flex align-center flex-wrap mb-1">
              <span class="day-date mr-3">{{ formatDayLabel(row.business_date) }}</span>
              <v-chip
                v-for="cat in categoriesOf(row)"
                :key="`${row.business_date}-${cat.key}`"
                :color="cat.color"
                small
                label
                outlined
                class="mr-2 mb-1 font-weight-bold"
              >
                <v-icon
                  left
                  small
                >
                  {{ cat.icon }}
                </v-icon>
                {{ cat.label }}
                <span
                  v-if="cat.count > 1"
                  class="ml-1"
                >({{ cat.count }})</span>
              </v-chip>
            </div>

            <div
              v-for="(finding, fIdx) in row.findings"
              :key="`finding-${row.business_date}-${fIdx}`"
              class="day-detail text--secondary"
            >
              <span class="agg-name">{{ finding.aggregation_name }}</span>
              <span class="text--disabled">（{{ contextTypeLabel(finding.context_type) }}）</span>
              <span>— {{ findingDescription(finding) }}</span>
              <span
                v-if="targetText(finding)"
                class="text--disabled"
              >／ 対象：{{ targetText(finding) }}</span>
            </div>
          </div>

          <div class="day-action">
            <v-btn
              color="primary"
              small
              :ripple="false"
              @click="$emit('go-to-closing', row.business_date)"
            >
              この日のクロージングへ
              <v-icon right>
                {{ icons.mdiArrowRight }}
              </v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <v-divider
        v-if="gIdx < monthGroups.length - 1"
        :key="`month-divider-${group.key}`"
      />
    </template>
  </v-card>
</template>

<script>
import { computed } from '@vue/composition-api'
import {
  parseISO, getYear, getMonth, getDate, getDay,
} from 'date-fns'
import {
  mdiArrowRight,
  mdiAlertCircle,
  mdiCircle,
  mdiSyncAlert,
} from '@mdi/js'

// 曜日の日本語表記（date-fns の ja ロケールに依存しないための定数）
const WEEKDAY_JA = ['日', '月', '火', '水', '木', '金', '土']

// 診断カテゴリ → 表示メタ。
// バックエンドの finding.category 値:
//   aggregation_job_missing  ... 未集計（集計ジョブが実行されていない）
//   result_missing_after_job ... 結果欠損（集計は完了したが結果の一部が未保存）
//   timecard_updated_after_job ... 要再集計（集計後にタイムカードが変更された）
const CATEGORY_META = {
  aggregation_job_missing: {
    label: '未集計',
    color: 'error',
    icon: mdiAlertCircle,
    description: '集計が実行されていません',
  },
  result_missing_after_job: {
    label: '結果欠損',
    color: 'warning',
    icon: mdiCircle,
    description: '集計は完了したが計算結果の一部が未保存',
  },
  timecard_updated_after_job: {
    label: '要再集計',
    color: 'warning',
    icon: mdiSyncAlert,
    description: '集計後にタイムカードが変更されました',
  },
}

const FALLBACK_CATEGORY = {
  label: '要確認',
  color: 'warning',
  icon: mdiCircle,
  description: '集計結果に不整合があります',
}

const CONTEXT_TYPE_LABELS = {
  User: 'ユーザー集計',
  Table: '卓集計',
  Club: '店舗集計',
}

export default {
  name: 'UnsavedList',
  props: {
    // 診断APIの営業日ごとの rows
    rows: {
      type: Array,
      default: () => [],
    },
    // 当月判定の基準日 (YYYY-MM-DD)
    today: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const categoryMeta = key => CATEGORY_META[key] || FALLBACK_CATEGORY

    const contextTypeLabel = type => CONTEXT_TYPE_LABELS[type] || type || ''

    const formatDayLabel = dateStr => {
      try {
        const d = parseISO(dateStr)

        return `${getMonth(d) + 1}/${getDate(d)}（${WEEKDAY_JA[getDay(d)]}）`
      } catch (e) {
        return dateStr
      }
    }

    // row 内の finding を category 単位に集約し、チップ表示用メタを返す。
    const categoriesOf = row => {
      const counts = {};
      (row.findings || []).forEach(f => {
        counts[f.category] = (counts[f.category] || 0) + 1
      })

      return Object.keys(counts).map(key => ({
        key,
        count: counts[key],
        ...categoryMeta(key),
      }))
    }

    const findingDescription = finding => categoryMeta(finding.category).description

    // 「対象：◯◯」表示。context_name を優先し、なければ集計表名で代替。
    const targetText = finding => finding.context_name || ''

    // 当月（基準日と同年同月）/ 前月の2グループに分割する。
    const monthGroups = computed(() => {
      const baseDate = props.today ? parseISO(props.today) : new Date()
      const baseYear = getYear(baseDate)
      const baseMonth = getMonth(baseDate)

      const currentRows = []
      const previousRows = [];
      [...props.rows]
        .sort((a, b) => (a.business_date < b.business_date ? 1 : -1))
        .forEach(row => {
          let rowDate
          try {
            rowDate = parseISO(row.business_date)
          } catch (e) {
            rowDate = null
          }
          if (rowDate && getYear(rowDate) === baseYear && getMonth(rowDate) === baseMonth) {
            currentRows.push(row)
          } else {
            previousRows.push(row)
          }
        })

      const monthLabel = date => `${getYear(date)}年${getMonth(date) + 1}月`
      const prevDate = new Date(baseYear, baseMonth - 1, 1)

      const groups = []
      if (currentRows.length > 0) {
        groups.push({ key: 'current', label: `当月（${monthLabel(baseDate)}）`, rows: currentRows })
      }
      if (previousRows.length > 0) {
        groups.push({ key: 'previous', label: `前月（${monthLabel(prevDate)}）`, rows: previousRows })
      }

      return groups
    })

    return {
      monthGroups,
      categoriesOf,
      contextTypeLabel,
      findingDescription,
      targetText,
      formatDayLabel,
      icons: {
        mdiArrowRight,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.month-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .5px;
  opacity: .6;
  border-bottom: 1px solid rgba(0, 0, 0, .08);
}

.day-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  border-bottom: 1px solid rgba(0, 0, 0, .08);

  &:last-child {
    border-bottom: none;
  }
}

.day-main {
  flex: 1;
  min-width: 0;
}

.day-date {
  font-size: 15px;
  font-weight: 700;
}

.day-detail {
  font-size: 13px;
  line-height: 1.7;
}

.agg-name {
  font-weight: 700;
}

.day-action {
  flex-shrink: 0;
}
</style>
